import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Plus, Send, Calendar, TrendingUp } from 'lucide-react';
import { format } from 'date-fns';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const Campaigns = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadCampaigns();
  }, []);

  const loadCampaigns = async () => {
    try {
      const response = await axios.get(`${API}/campaigns`, { headers: getAuthHeaders() });
      setCampaigns(response.data);
    } catch (error) {
      toast.error('Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: 'bg-[#9CA3AF]/20 text-[#9CA3AF]',
      scheduled: 'bg-[#F59E0B]/20 text-[#F59E0B]',
      sending: 'bg-[#06B6D4]/20 text-[#06B6D4]',
      sent: 'bg-[#10B981]/20 text-[#10B981]',
      failed: 'bg-[#EF4444]/20 text-[#EF4444]'
    };
    return colors[status] || colors.draft;
  };

  return (
    <Layout>
      <div className="p-6 md:p-8 lg:p-12">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold tracking-tight text-[#F3F4F6] mb-2">
              Campaigns
            </h1>
            <p className="text-lg text-[#9CA3AF]">
              Create and manage email campaigns
            </p>
          </div>
          <Button
            onClick={() => navigate('/campaigns/create')}
            data-testid="create-campaign-button"
            className="bg-[#183871] hover:bg-[#1F4BA1] text-white"
          >
            <Plus size={20} className="mr-2" />
            New Campaign
          </Button>
        </div>

        <div className="rounded-lg border border-[#2A2D36] bg-[#13151A]/80 backdrop-blur-sm overflow-hidden">
          {loading ? (
            <p className="text-[#9CA3AF] text-center py-12">Loading campaigns...</p>
          ) : campaigns.length === 0 ? (
            <div className="text-center py-12">
              <Send size={48} className="mx-auto text-[#9CA3AF] mb-4" />
              <p className="text-[#9CA3AF] mb-4">No campaigns yet</p>
              <Button
                onClick={() => navigate('/campaigns/create')}
                className="bg-[#183871] hover:bg-[#1F4BA1] text-white"
              >
                Create Your First Campaign
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#1F2128] border-b border-[#2A2D36]">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#9CA3AF] uppercase">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#9CA3AF] uppercase">Subject</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#9CA3AF] uppercase">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#9CA3AF] uppercase">Recipients</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#9CA3AF] uppercase">Sent</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#9CA3AF] uppercase">Opens</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#9CA3AF] uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#2A2D36]">
                  {campaigns.map((campaign) => (
                    <tr key={campaign.id} className="hover:bg-[#1F2128] transition-colors">
                      <td className="px-6 py-4 text-sm text-[#F3F4F6]">{campaign.name}</td>
                      <td className="px-6 py-4 text-sm text-[#9CA3AF]">{campaign.subject}</td>
                      <td className="px-6 py-4 text-sm">
                        <span className={`px-2 py-1 rounded-md text-xs font-medium ${getStatusColor(campaign.status)}`}>
                          {campaign.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-[#9CA3AF]">{campaign.total_recipients || 0}</td>
                      <td className="px-6 py-4 text-sm text-[#9CA3AF]">{campaign.total_sent || 0}</td>
                      <td className="px-6 py-4 text-sm text-[#9CA3AF]">{campaign.total_opened || 0}</td>
                      <td className="px-6 py-4 text-sm">
                        <Button
                          onClick={() => navigate(`/campaigns/${campaign.id}/analytics`)}
                          size="sm"
                          variant="outline"
                          className="border-[#2A2D36] text-[#183871]"
                        >
                          <TrendingUp size={14} className="mr-1" />
                          View
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default Campaigns;
